import React, { useState, useEffect, useRef } from "react";
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowRight,
    faTimes,
    faCopy,
    faMicrophoneSlash,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../../firebase/AuthProvider";
import { useNetworkQuality } from "../../../hooks/useNetworkQuality";
import SignalStrengthIndicator from "./notificationElements/networkIndicator/SignalStrengthIndicator";
import MeetingDetails from "./meetingDetails/MeetingDetails";
import {
    LobbyState,
    VideoCallState,
} from "../../../interfaces/videocallDtos/videoCall";
import { GetMeetingByAssignedIdAsync } from "../../../services/videoService/VideoService";
import { toast } from "react-toastify";
import { ValidateOtpResponseDto } from "../../../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import { Button } from "react-bootstrap";
import audioicon from "../../../assets/images/audioicon.svg";
import videocallicon from "../../../assets/images/videocallicon.svg";
import MuteButton from "./controls/MuteButton";
import CameraButton from "./controls/CameraButton";
import { Participant } from "../../../interfaces/video/video";
import { ParticipantTile } from "./ParticipantTile";
import { MAX_LENGTH_DESCRIPTION_SHOW } from "../../../Data/SD";
import { useChatConnection } from "../../../contexts/ChatConnectionContext";
import { RemoveAdHocFromConversationDto } from "../../../services/chatService/ChatService";
import { showOkDialog } from "../../modal/ConfimationModal/Confimation";
import "./Lobby.css"
import AvatarImage from "../../ui/Avatar/AvatarImage";
import { MicrophoneIcon } from "../SvgIcons/MicroPhoneIcon";
import { MutedMicrophoneIcon } from "../SvgIcons/MutedMicrophoneIcon";

interface Device {
    id: string;
    label: string;
}

interface PlaceholderMeetingDetails {
    id: string;
    title: string;
    description?: string;
    startTime: string;
    endTime: string;
    organizer?: {
        id: string;
        displayName: string;
    };
}

const Lobby: React.FC = () => {
    const { roomId } = useParams();
    const { currentUserInfo, currentUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const isExternal = location.pathname.includes("/external"); // cheking user comes from external link or not

    const [state, setState] = useState(location.state as LobbyState);

    const videoRef = useRef<HTMLVideoElement>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);

    const [videoDevices, setVideoDevices] = useState<Device[]>([]);
    const [audioDevices, setAudioDevices] = useState<Device[]>([]);
    const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>("");
    const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("");
    const [displayName, setDisplayName] = useState<string>(
        currentUserInfo?.firstName || currentUserInfo?.lastName
            ? `${currentUserInfo?.firstName} ${currentUserInfo?.lastName}`
            : "Guest"
    );
    const [externalDetails, setExternalDetails] =
        useState<ValidateOtpResponseDto>();
    const [error, setError] = useState<string | null>(null);
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [isMicOn, setIsMicOn] = useState(false);

    const { signalStrength, latency } = useNetworkQuality();

    const [placeholderMeetingDetails, setPlaceholderMeetingDetails] =
        useState<PlaceholderMeetingDetails | null>(null);
    const [isMeetingDetailsLoading, setIsMeetingDetailsLoading] =
        useState<boolean>(true);
    const [meetingDetailsError, setMeetingDetailsError] = useState<string | null>(
        null
    );

    const [isMeetingEnd, setIsMeetingEnd] = useState<boolean>(false);


    // Shared SignalR connection
    const { connection } = useChatConnection();

    useEffect(() => {
        if (!connection) return;
        connection.on("RemoveAdHocFromConversation", handleRemoveAdHocFromConversation);

        return () => {
            connection.off("RemoveAdHocFromConversation", handleRemoveAdHocFromConversation);
        }
    }, [connection]);

    const handleRemoveAdHocFromConversation = (payload: RemoveAdHocFromConversationDto) => {
        if (roomId == payload.roomId) {
            setIsMeetingEnd(true);
            navigate("/chat");
            showOkDialog("Meeting Ended", "", "warning");
        }
    }

    useEffect(() => {
        const ex_details = localStorage.getItem("ExternalUserDetails");
        // For External User
        if (isExternal && ex_details) {
            const externalUserDetails: ValidateOtpResponseDto =
                JSON.parse(ex_details);
            setExternalDetails(externalUserDetails);
            setDisplayName(externalUserDetails?.externalUsername);
            if (roomId) {
                GetMeetingByAssignedId(
                    "",
                    roomId,
                    externalUserDetails.tempSessionToken,
                    externalUserDetails.ticketId,
                    externalUserDetails.externalUserDetailId
                );
            }
        } else {
            setDisplayName(
                currentUserInfo?.firstName || currentUserInfo?.lastName
                    ? `${currentUserInfo?.firstName} ${currentUserInfo?.lastName}`
                    : "Guest"
            );
        }
    }, [currentUserInfo, isExternal]);

    // @TODO: Replace this with working code.
    useEffect(() => {
        currentUser?.getIdToken().then((token) => {
            if (token && roomId) {
                GetMeetingByAssignedId(token, roomId);
            }
        });

        setIsMeetingDetailsLoading(true);
        setMeetingDetailsError(null);

        const timer = setTimeout(() => {
            if (roomId === "scheduled-meeting-123") {
                setPlaceholderMeetingDetails({
                    id: roomId,
                    title: "Project Alpha Sync-Up",
                    description:
                        "Discuss progress on Project Alpha, next steps, and resolve blockers.",
                    startTime: "2025-07-10T10:00:00Z", // Tomorrow 10 AM UTC
                    endTime: "2025-07-10T11:00:00Z", // Tomorrow 11 AM UTC
                    organizer: {
                        id: "org1",
                        displayName: "Jane Doe",
                    },
                });
            } else if (roomId === "error-meeting-456") {
                // Example: A roomId that causes an error
                setMeetingDetailsError(
                    "Failed to fetch meeting data. Please try again."
                );
            } else {
                // Default to on-demand call for other room IDs
                setPlaceholderMeetingDetails(null);
            }
            setIsMeetingDetailsLoading(false);
        }, 1500); // Simulate 1.5 second loading time

        return () => clearTimeout(timer); // Cleanup timeout
    }, [roomId, currentUser]);

    const GetMeetingByAssignedId = (
        token: string,
        roomId: string,
        videoToken: string = "",
        externalTicketId: string = "",
        externalUserId: string = ""
    ) => {
        GetMeetingByAssignedIdAsync(
            token,
            roomId,
            videoToken,
            externalTicketId,
            externalUserId
        )
            .then((res) => {
                if (res.success) {
                    setState((prev) => ({
                        ...prev,
                        eventTitle: res.data.eventTitle,
                        participantNames: res.data.participantNames,
                        eventDescription: res.data.eventDescription,
                        calenderEventId: res.data.calendarEventId ?? null,
                        relatedChatConversationId:
                            res.data.conversationId?.toString() ?? null,

                    }));
                } else {
                    toast.error(res.message);
                }
            })
            .catch((err) => {
                toast.error(err.message);
            });
    };

    const lobbyParticipant: Participant = {
        isLocal: true,
        feedId: "local-preview",
        userId: currentUser?.uid,
        displayName: `${displayName} (You)`,
        stream: stream,
        isMuted: !isMicOn,
        isCameraOn: isCameraOn,
        latency: latency,
    };

    const mainBg = "#212128";
    const inputBg = "#34353A";
    const purpleButton = "#A855F7";

    // Function to stop the current media stream
    const stopStream = () => {
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            setStream(null);
        }
    };

    // Get and set media devices
    useEffect(() => {
        const getDevices = async () => {
            try {
                // Try to obtain microphone access first since mic is required for joining.
                let initialStream: MediaStream | null = null;
                try {
                    initialStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                } catch (audioErr) {
                    console.warn("Microphone access denied or not available:", audioErr);
                    setError("Could not access your microphone. Please check your browser permissions.");
                    // Continue to enumerate devices so we can still show available device lists (if any)
                }

                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoInputs = devices.filter((device) => device.kind === "videoinput");
                const audioInputs = devices.filter((device) => device.kind === "audioinput");

                setVideoDevices(videoInputs.map((d) => ({ id: d.deviceId, label: d.label })));
                setAudioDevices(audioInputs.map((d) => ({ id: d.deviceId, label: d.label })));

                if (videoInputs.length > 0) setSelectedVideoDevice(videoInputs[0].deviceId);
                else setSelectedVideoDevice("");

                if (audioInputs.length > 0) {
                    setSelectedAudioDevice(audioInputs[0].deviceId);
                    // If mic is available, clear any microphone-related error that may have been set earlier
                    setError((prev) => (prev && prev.includes("microphone") ? null : prev));
                } else {
                    // No microphones found
                    setSelectedAudioDevice("");
                }

                if (initialStream) initialStream.getTracks().forEach((track) => track.stop());
            } catch (err) {
                console.error("Error getting media devices:", err);
                setError("Could not enumerate media devices. Please check your browser settings.");
            }
        };

        getDevices();

        return () => {
            stopStream();
        };
    }, []);

    // Effect to update the stream when a device is changed
    useEffect(() => {
        if (!selectedAudioDevice) return;

        const startStream = async () => {
            stopStream();

            try {
                const newStream = await navigator.mediaDevices.getUserMedia({
                    video: isCameraOn && selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice } } : false,
                    audio: { deviceId: { exact: selectedAudioDevice } },
                });

                // Apply current mute/camera-off states to the new stream
                newStream.getAudioTracks().forEach((track) => (track.enabled = isMicOn));

                setStream(newStream);

                setError(null);
            } catch (err) {
                console.error("Error starting stream with selected devices:", err);
                setError(
                    "Could not start media with the selected device. It might be in use by another application."
                );
            }
        };

        startStream();
    }, [selectedVideoDevice, selectedAudioDevice, isCameraOn]);

    useEffect(() => {
        // When the camera is on and we have a video element and a stream, connect them.
        if (isCameraOn && videoRef.current && stream) {
            videoRef.current.srcObject = stream;
        }
    }, [isCameraOn, stream]);

    const handleToggleCamera = () => {
        // If enabling camera but no camera is available, show a helpful error and do not toggle
        if (!isCameraOn && !selectedVideoDevice) {
            setError("No camera device found.");
            return;
        }
        setIsCameraOn((prevState) => !prevState);
        setError(null);
    };

    const handleToggleMic = () => {
        const newMicState = !isMicOn;
        setIsMicOn(newMicState);
        if (stream) {
            stream.getAudioTracks().forEach((track) => {
                track.enabled = newMicState;
            });
        }
    };

    const handleJoin = () => {
        stopStream();
        let url = `/room/${roomId}`;
        localStorage.removeItem("isPublisher");

        // Define a common state structure
        let videoCallState: VideoCallState = {
            audioDeviceId: selectedAudioDevice,
            videoDeviceId: selectedVideoDevice,
            displayName: displayName || "Guest",
            isPublisher: state?.isPublisher ?? false,
            isOneToOneCall: state?.isOneToOneCall ?? false,
            isCameraOn: isCameraOn,
            isMicMuted: !isMicOn,
            calenderEventId: null,
            relatedChatConversationId: null,
        };

        if (videoCallState.isPublisher) {
            localStorage.setItem("isPublisher", "true");
        }
        if (isExternal && externalDetails) {
            videoCallState.isPublisher = false;
            videoCallState.calenderEventId = externalDetails.calendarEventId;
            url = `/external/room/${roomId}`;
        } else if (state?.calenderEventId) {
            videoCallState.calenderEventId = state.calenderEventId;
        } else if (state?.relatedChatConversationId) {
            videoCallState.relatedChatConversationId =
                state.relatedChatConversationId;
        }

        navigate(url, { state: videoCallState });
    };

    const handleClose = () => {
        stopStream();
        navigate(-1);
    };

    const handleCopyRoomId = () => {
        const roomUrl = `${window.location.origin}/lobby/${roomId}`;
        navigator.clipboard.writeText(roomUrl).then(() => {
            toast.success("Room ID copied to clipboard!");
        }).catch(() => {
            toast.error("Failed to copy room ID");
        });
    };

    const roomUrl = `${window.location.origin}/lobby/${roomId}`;

    return (
        <>
            <div className="lobby_modal_overlay">
                <div className="lobby_modal_container">
                    {/* Purple Header Section */}
                    <div className="lobby_modal_header">

                        <div className="lobby_video_preview_container">
                            <AvatarImage className="w-full h-full object-cover" userId={currentUser?.uid || displayName || ""} displayName={displayName} size="md" />
                        </div>
                        <div className={`lobby_user_badge`}>
                            {
                                isMicOn ? (
                                    <MicrophoneIcon size={16} strokeColor={"#555"}/>
                                ) : (
                                    <MutedMicrophoneIcon size={16} color={"#FB2C36"}/>
                                )
                            }
                            <span>{displayName} (You)</span>
                        </div>
                        <button
                            className="lobby_close_button"
                            onClick={handleClose}
                            aria-label="Close"
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 4L4 12" stroke="white" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M4 4L12 12" stroke="white" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>

                        </button>
                    </div>

                    {/* Main Content */}
                    <div className="lobby_modal_body">
                        {/* Control Buttons */}
                        <div className="lobby_controls">
                            <MuteButton backgroundColor="#FB2C36" hoverBGColor="#cf1c25" onToggle={handleToggleMic} muted={!isMicOn} />
                            <CameraButton onClick={handleToggleCamera} isOn={isCameraOn} />
                        </div>

                        {error && (
                            <div className="lobby_error_message">
                                <div className="alert alert-danger mb-0 rounded-3 text-center" role="alert">
                                    {error}
                                </div>
                            </div>
                        )}

                        {/* Meeting Information Section */}
                        <div className="lobby_meeting_info">
                            <div className="lobby_meeting_info_label">Meeting Information</div>
                            <div className="lobby_attendee_section">
                                <label htmlFor="displayName" className="lobby_attendee_label">Attendee</label>
                                <div className="lobby_attendee_input_wrapper">
                                    <input
                                        type="text"
                                        id="displayName"
                                        value={displayName}
                                        disabled={!!currentUserInfo}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        placeholder="Enter your name"
                                        className="lobby_attendee_input"
                                    />
                                    <Button
                                        bsPrefix="custom-button"
                                        className="lobby_join_button"
                                        onClick={handleJoin}
                                        disabled={
                                            !selectedAudioDevice ||
                                            !displayName ||
                                            isMeetingEnd ||
                                            (!isExternal && (!currentUser || !currentUserInfo))
                                        }

                                    >
                                        Join Now <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Device Selection */}
                        <div className="lobby_device_selection">
                            <div className="lobby_device_item">
                                <div className="lobby_device_icon_wrapper">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M10.666 8.66676L14.148 10.9881C14.1982 11.0215 14.2565 11.0407 14.3168 11.0435C14.377 11.0464 14.4369 11.0329 14.49 11.0045C14.5432 10.976 14.5876 10.9337 14.6186 10.8819C14.6496 10.8302 14.666 10.7711 14.666 10.7108V5.24676C14.666 5.18811 14.6506 5.13049 14.6212 5.07973C14.5918 5.02896 14.5496 4.98684 14.4987 4.95763C14.4479 4.92841 14.3902 4.91313 14.3316 4.91333C14.2729 4.91353 14.2153 4.9292 14.1647 4.95876L10.666 7.0001" stroke="#7E57C2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M9.33398 4H2.66732C1.93094 4 1.33398 4.59695 1.33398 5.33333V10.6667C1.33398 11.403 1.93094 12 2.66732 12H9.33398C10.0704 12 10.6673 11.403 10.6673 10.6667V5.33333C10.6673 4.59695 10.0704 4 9.33398 4Z" stroke="#7E57C2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </div>
                                <select
                                    id="videoDevice"
                                    value={selectedVideoDevice}
                                    onChange={(e) => { setSelectedVideoDevice(e.target.value); setError(null); }}
                                    className="lobby_device_select"
                                >
                                    {videoDevices.length > 0 ? (
                                        videoDevices.map((device) => (
                                            <option key={device.id} value={device.id}>
                                                {device.label}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="">No cameras found</option>
                                    )}
                                </select>
                            </div>
                            <div className="lobby_device_item">
                                <div className="lobby_device_icon_wrapper">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 12.6667V14.6667" stroke="#7E57C2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M12.6673 6.66675V8.00008C12.6673 9.23776 12.1757 10.4247 11.3005 11.2999C10.4253 12.1751 9.23833 12.6667 8.00065 12.6667C6.76297 12.6667 5.57599 12.1751 4.70082 11.2999C3.82565 10.4247 3.33398 9.23776 3.33398 8.00008V6.66675" stroke="#7E57C2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M10 3.33325C10 2.22868 9.10457 1.33325 8 1.33325C6.89543 1.33325 6 2.22868 6 3.33325V7.99992C6 9.10449 6.89543 9.99992 8 9.99992C9.10457 9.99992 10 9.10449 10 7.99992V3.33325Z" stroke="#7E57C2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </div>
                                <select
                                    id="audioDevice"
                                    value={selectedAudioDevice}
                                    onChange={(e) => { setSelectedAudioDevice(e.target.value); setError(null); }}
                                    className="lobby_device_select"
                                >
                                    {audioDevices.length > 0 ? (
                                        audioDevices.map((device) => (
                                            <option key={device.id} value={device.id}>
                                                {device.label}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="">No microphones found</option>
                                    )}
                                </select>
                            </div>
                        </div>

                        {/* Room ID Sharing Section */}
                        <div className="lobby_room_share">
                            <p className="lobby_room_share_text">You can share the Room ID</p>
                            <div className="lobby_room_url_wrapper">
                                <input
                                    type="text"
                                    value={roomUrl}
                                    readOnly
                                    className="lobby_room_url_input"
                                />
                                <div onClick={handleCopyRoomId}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M16.666 6.66675H8.33268C7.41221 6.66675 6.66602 7.41294 6.66602 8.33341V16.6667C6.66602 17.5872 7.41221 18.3334 8.33268 18.3334H16.666C17.5865 18.3334 18.3327 17.5872 18.3327 16.6667V8.33341C18.3327 7.41294 17.5865 6.66675 16.666 6.66675Z" stroke="#7E57C2" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M3.33268 13.3334C2.41602 13.3334 1.66602 12.5834 1.66602 11.6667V3.33341C1.66602 2.41675 2.41602 1.66675 3.33268 1.66675H11.666C12.5827 1.66675 13.3327 2.41675 13.3327 3.33341" stroke="#7E57C2" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </div>


                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Lobby;
